import React, { useEffect, useRef, useState } from "react"
import ReactGA from "react-ga"
import { Player, BigPlayButton } from "video-react"
import { graphql, useStaticQuery } from "gatsby"
import { CarouselProvider, Slider, Slide, DotGroup } from "pure-react-carousel"
import classNames from "classnames"
import useDimensions from "react-use-dimensions"

import heroVideo from "~/images/heroVideo.mp4"
import ErrorBoundary from "../common/ErrorBoundary"


const HeroSectionHeading = ({ className }) => (
  <div className={classNames("flex flex-col items-center", className)}>
    <h1 className="font-heading text-4xl md:text-5xl lg:text-4xl xl:text-5xl uppercase text-center leading-none">
      #DearSocialMedia
    </h1>
    <p className="pb-4 pt-2 px-2 text-base md:text-lg lg:text-base xl:text-xl text-center">
      Young people want change. Let’s listen.
    </p>
  </div>
)

const HeroSectionQuotes = ({ className }) => {
  const [ref, { x, height, width }] = useDimensions()

  const quotes = [
    {
      quote: "I want your help to make social media platforms more positive.",
      name: "Susi, 16 years old",
    },
    {
      quote:
        "It’s our duty to make sure the tech industry is showing more representation of what we want.",
      name: "Issatou, 17 years old",
    },
    {
      quote:
        "What if I post something and I make a mistake? Will my colleges be able to see it? Will my employers be able to see it?",
      name: "Anastasia, 16 years old",
    },
    {
      quote:
        "I would talk to the tech industry about ways that they can be more in contact with what’s going on on their platform.",
      name: "Edwin, 15 years old",
    },
    {
      quote:
        "I feel like there’s a lot of things on the internet that are good, but there’s also a lot of things that my generation has to figure out.",
      name: "Anastasia, 16 years old",
    },
  ]
  return (
    <ErrorBoundary>
      <CarouselProvider
        totalSlides={quotes.length}
        className={classNames("px-2 mt-4 lg:mt-0", className)}
      >
        <Slider>
          {quotes.map((quote, index) => (
            <Slide index={index} key={index} style={{ height: `${height}px` }}>
              <div className="flex flex-col items-center w-full" ref={ref}>
                <div className="block w-full">
                  <p className="quote text-center md:text-2xl lg:text-xl xl:text-2-5-xl">
                    <span> {` ${quote.quote}”`}</span>
                  </p>
                </div>
                <div className="block w-full">
                  <p className="uppercase mt-4 text-sm md:text-base lg:text-sm xl:text-lg text-center">
                    - {quote.name}
                  </p>
                </div>
              </div>
            </Slide>
          ))}
        </Slider>
        <DotGroup className="flex justify-center my-4"></DotGroup>
      </CarouselProvider>
    </ErrorBoundary>
  )
}

const HeroSection = () => {
  const data = useStaticQuery(graphql`
    query {
      heroPoster: file(name: { eq: "heroPoster" }) {
        childImageSharp {
          fixed(width: 800, height: 800, quality: 90) {
            src
          }
        }
      }
    }
  `)
  const player = useRef(null)
  const [playerStarted, setPlayerStarted] = useState(null)

  useEffect(() => {
    if(player.current && player.current.subscribeToStateChange) {
      const sub = (s, p) => setPlayerStarted(s.hasStarted)
      player.current.subscribeToStateChange(sub);
    }
  }, [player])
  useEffect(() => {
    if(playerStarted) {
      ReactGA.event({
        category: "video",
        action: "play",
        label: "hero-video",
      })
    }
  }, [playerStarted])

  const { heroPoster } = data

  return (
    <section className="flex flex-col lg:flex-row px-content-mobile md:px-content my-4 mx-auto w-full md:w-10/12 lg:w-full xl:w-11/12">
      <HeroSectionHeading className="lg:hidden" />
      <div className="w-full lg:w-1/2 xl:w-1/2 border-b-4 border-yellow ml-auto">
        <Player
          ref={player}
          playsInline
          aspectRatio="1:1"
          poster={heroPoster.childImageSharp.fixed.src}
          src={heroVideo}
        >
          <BigPlayButton position="center" />
        </Player>
      </div>
      <div
        className="hidden lg:flex lg:flex-col w-full lg:w-1/2 xl:w-1/2 mt-4 mb-8 border-r-2 border-b-2 border-t-2 py-4 px-4 mr-auto"
        style={{ maxWidth: "440px" }}
      >
        <HeroSectionHeading />
        <HeroSectionQuotes />
      </div>
      <HeroSectionQuotes className="lg:hidden" />
    </section>
  )
}

export default HeroSection
